import type { Run, WorkflowSchedule } from '@cortex/types';
import { useMemo, type CSSProperties } from 'react';

import { formatDateTime, formatDuration, formatRelative } from '../../../lib/format';
import { useScheduledRuns } from '../../../lib/workflow-schedules';
import { Spinner } from '../../common/Spinner';
import { StatusBadge } from '../../common/StatusBadge';

const rowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto auto',
  alignItems: 'center',
  columnGap: 'var(--sp-2)',
  padding: 'var(--sp-1) 0',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 'var(--fs-xs)',
};

/**
 * The last 20 runs (scheduledRunHistoryLimit) this workflow's schedules
 * fired (trigger SCHEDULED, newest first), one list under the schedule
 * cards. Each row names the schedule (via Run.scheduleId), when it started,
 * its status and duration, and opens the run in the Runs tab.
 */
export function ScheduledRunHistory({
  workflowId,
  schedules,
  onOpenRun,
}: {
  workflowId: string;
  schedules: WorkflowSchedule[];
  onOpenRun: (runId: string) => void;
}) {
  const runs = useScheduledRuns(workflowId);
  const scheduleName = useMemo(() => {
    const m = new Map(schedules.map((s) => [s.id, s.name]));
    return (id: string | null | undefined): string =>
      id ? (m.get(id) ?? `deleted schedule ${id.slice(0, 8)}`) : 'schedule';
  }, [schedules]);

  const items = runs.data?.items ?? [];
  const total = runs.data?.total ?? items.length;

  return (
    <section
      aria-label="Scheduled run history"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="t-mono-label">Scheduled runs</span>
        <span className="t-mono-meta t-xs">
          {total > items.length ? `last ${items.length} of ${total}` : `${items.length}`}
        </span>
      </div>
      {runs.isLoading ? (
        <Spinner />
      ) : runs.error ? (
        <div className="alert alert--danger" style={{ fontSize: 12 }}>
          Failed to load: {(runs.error as Error).message}
        </div>
      ) : items.length === 0 ? (
        <span className="t-muted t-xs">
          No scheduled runs yet. They appear here once a schedule fires or you click Run now.
        </span>
      ) : (
        <div>
          {items.map((run) => (
            <ScheduledRunRow
              key={run.id}
              run={run}
              scheduleName={scheduleName(run.scheduleId)}
              onOpen={() => onOpenRun(run.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function ScheduledRunRow({
  run,
  scheduleName,
  onOpen,
}: {
  run: Run;
  scheduleName: string;
  onOpen: () => void;
}) {
  const started = run.startedAt ?? run.createdAt;
  const duration =
    run.startedAt && run.finishedAt ? formatDuration(run.startedAt, run.finishedAt) : null;
  return (
    <div style={rowStyle} data-testid="scheduled-run-row">
      <StatusBadge status={run.status} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <span style={{ color: 'var(--text-primary)' }}>{scheduleName}</span>
        <span className="t-muted" title={formatDateTime(started)}>
          {' '}
          {run.startedAt ? 'started' : 'queued'} {formatRelative(started)}
        </span>
      </span>
      <span className="t-mono-meta">{duration ?? ''}</span>
      <button type="button" className="btn btn--ghost btn--xs" onClick={onOpen}>
        Open
      </button>
    </div>
  );
}
